import React from "react";
import { motion } from "framer-motion";
import { PublicNav, Footer } from "@/components/PublicChrome";
import { Embers } from "@/components/Embers";

const LegalLayout = ({ eyebrow, title, effectiveDate, children, testid }) => (
  <div className="min-h-screen bg-[#050505] text-[#f5f5f5]">
    <PublicNav />
    <section className="relative overflow-hidden border-b border-[#ff7a00]/10 py-14">
      <Embers count={12} />
      <div className="absolute -top-32 -left-32 w-[420px] h-[420px] bg-[#ff7a00]/15 rounded-full blur-[140px]" />
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="text-[#ff7a00] text-xs tracking-[0.3em] uppercase mb-3" data-testid={`${testid}-eyebrow`}>{eyebrow}</div>
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="font-display text-4xl sm:text-5xl font-bold leading-tight"
        >
          {title}
        </motion.h1>
        {effectiveDate && (
          <div className="mt-3 inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#ff7a00]/30 bg-[#ff7a00]/5 text-[#ff9a2f] text-[11px] tracking-widest uppercase">
            Effective {effectiveDate}
          </div>
        )}
      </div>
    </section>

    <section className="py-12 sm:py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10">
        <article className="glass rounded-xl p-6 sm:p-8 lg:p-10 prose-funded space-y-7" data-testid={testid}>
          {children}
        </article>
      </div>
    </section>

    <Footer />
  </div>
);

const H2 = ({ children }) => (
  <h2 className="font-display text-xl sm:text-2xl font-bold text-[#f5f5f5] mt-2 mb-3 flex items-baseline gap-3">
    <span className="inline-block w-1.5 h-5 bg-[#ff7a00] rounded-sm" />
    {children}
  </h2>
);
const H3 = ({ children }) => <h3 className="font-display text-base sm:text-lg font-semibold text-[#ff9a2f] mt-4 mb-2">{children}</h3>;
const P = ({ children }) => <p className="text-[#cfcfcf] text-[15px] leading-relaxed">{children}</p>;
const UL = ({ children }) => <ul className="list-disc pl-5 space-y-2 text-[#cfcfcf] text-[15px] marker:text-[#ff7a00]">{children}</ul>;
const Hr = () => <hr className="border-[#ff7a00]/15 my-6" />;

// ===================== TERMS =====================
export const TermsPage = () => (
  <LegalLayout
    eyebrow="Legal"
    title="Terms & Conditions"
    effectiveDate="October 31, 2025"
    testid="terms-page"
  >
    <P>
      Welcome to <b className="text-[#f5f5f5]">For Index Traders</b> ("the Company," "we," "our," or "us"). These Terms and Conditions ("Terms") govern your access to and use of our proprietary trading evaluation and funded account services ("Services"). By using our platform or purchasing any program, you agree to these Terms in full. If you do not agree, you must discontinue use immediately.
    </P>

    <Hr />
    <H2>1. Nature of Services</H2>
    <P>
      For Index Traders provides proprietary trading evaluations and simulated funded programs for traders of <b>synthetic indices</b> and other permitted instruments available on the Deriv platform. All trading takes place exclusively on Deriv MT5 demo environments that replicate live market conditions. These accounts are <b>simulated environments</b> — no trades are executed on live markets, and all payouts are made directly by For Index Traders based on your trading performance within the simulation.
    </P>

    <H2>2. Eligibility</H2>
    <UL>
      <li>You must be at least 18 years of age to participate.</li>
      <li>You represent that you are legally permitted to use our Services in your country or jurisdiction.</li>
      <li>For Index Traders does not operate as a broker and does not offer live trading accounts or financial advice.</li>
      <li>You agree to provide accurate, complete, and verifiable information during registration and KYC verification.</li>
    </UL>

    <H2>3. Program Rules</H2>
    <P>
      Each For Index Traders account and evaluation phase is governed by a set of predefined rules that you must strictly adhere to. Breaching any of these rules constitutes a <b>hard breach</b> and may result in immediate termination of your trading account without refund.
    </P>
    <UL>
      <li><b>Daily Drawdown:</b> 5% of the account balance at the start of the trading day.</li>
      <li><b>Maximum Overall Drawdown:</b> 10% static from the starting balance.</li>
      <li><b>Profit Target:</b> 15% total profit to complete a phase or qualify for payout.</li>
      <li><b>Minimum Trading Days:</b> 3 profitable trading days before qualification or payout request.</li>
      <li><b>Consistency Rules:</b> You must maintain a consistent lot size and profit distribution. Any position exceeding 200% or falling below 50% of your initial lot size constitutes a hard breach. Additionally, no single profitable trading day may account for more than 20% of your total accumulated profit at the time of a payout request. Exceeding this threshold will result in payout ineligibility, not a breach.</li>
    </UL>
    <P>
      For Index Traders reserves the right to review trading patterns to ensure compliance with fairness and risk management standards. Accounts involved in rule manipulation, unfair trading advantages, or exploitative behavior will be subject to closure.
    </P>

    <H2>4. Fees and Payments</H2>
    <UL>
      <li>All evaluation fees are one-time and non-refundable once trading credentials have been issued.</li>
      <li>We do not charge per-trade or commission fees. Network or transaction costs are the responsibility of the user.</li>
      <li>In certain promotions, refunds or discounts may be offered at the sole discretion of For Index Traders.</li>
    </UL>

    <H2>5. Payouts</H2>
    <P>
      Payouts are made directly by For Index Traders and not by Deriv or any third-party broker. Traders are eligible for <b>bi-weekly payouts</b> after meeting the payout requirements and profit targets.
    </P>
    <UL>
      <li>Payouts can be requested bi-weekly of funded trading.</li>
      <li>Profit Split Structure: 50% (first payout), 60% (second payout), 70% (third payout), up to 80% for subsequent payouts.</li>
      <li>Payouts are processed in cryptocurrency or approved digital assets within 24–48 hours after approval.</li>
    </UL>

    <H2>6. Restricted Trading Practices</H2>
    <P>The following practices are strictly prohibited and considered hard breaches:</P>
    <UL>
      <li>High-Frequency Trading (HFT) or latency arbitrage.</li>
      <li>Copying trades from restricted sources, account mirroring, or multi-account trading.</li>
      <li>Tick manipulation, order flow abuse, or using Expert Advisors (EAs) that exploit price feed delays.</li>
      <li>Trading without stop loss.</li>
      <li><b>Minimum Trade Hold Time — 3 Minutes:</b> Every position must remain open for at least three (3) minutes from the time of entry. Any trade that is opened and closed within this three-minute window (whether manually or by an automated system) is considered a hard breach and will result in immediate account termination. This rule discourages scalping micro-positions and enforces intentional, risk-managed trading behavior.</li>
      <li>Trading in a manner inconsistent with normal market behavior or risk exposure.</li>
      <li>Fraud, chargeback abuse, or identity falsification.</li>
    </UL>
    <P>
      For Index Traders reserves the right to revoke account access, withhold payouts, and take appropriate legal action in cases of system abuse or fraud.
    </P>

    <H2>7. Account Actions and Termination</H2>
    <P>For Index Traders may suspend, terminate, or reset your account at its discretion if:</P>
    <UL>
      <li>You breach program rules or violate these Terms.</li>
      <li>You engage in suspicious or manipulative activity.</li>
      <li>You fail to comply with AML/KYC verification requirements.</li>
    </UL>
    <P>
      Upon termination, any pending profits or evaluations are forfeited. You may reapply or purchase a new challenge unless permanently banned.
    </P>

    <H2>8. Intellectual Property</H2>
    <P>
      All site content, brand names, graphics, code, text, and materials are owned by For Index Traders. You may not copy, modify, distribute, or use any part of the platform for commercial purposes without prior written consent.
    </P>

    <H2>9. Disclaimers and Risk Notice</H2>
    <UL>
      <li>For Index Traders is not affiliated with Deriv or any regulated broker.</li>
      <li>All trading occurs in a simulated environment with no live market exposure.</li>
      <li>Trading synthetic indices involves risk. Past performance does not guarantee future results.</li>
      <li>All services are provided "as is" and "as available" without warranties of any kind.</li>
    </UL>

    <H2>10. Data Protection & Privacy</H2>
    <P>
      Your data is processed according to our <a href="/policies" className="text-[#ff9a2f] hover:text-[#ff7a00] underline">Privacy Policy</a>. For Index Traders uses encryption and secure servers to protect personal data. However, no system is entirely immune to breaches, and you acknowledge that you use our Services at your own risk.
    </P>

    <H2>11. Modifications</H2>
    <P>
      For Index Traders reserves the right to modify, replace, or update these Terms at any time without prior notice. Updates will take effect immediately upon posting. Continued use of our Services signifies your acceptance of the revised Terms.
    </P>

    <H2>12. Jurisdiction and Governing Law</H2>
    <P>
      These Terms shall be governed by and interpreted in accordance with the laws of the <b>United Arab Emirates</b>, under the jurisdiction of the <b>DIEZ Freezone Authority</b>. Any disputes arising from or related to these Terms shall be resolved exclusively in UAE courts.
    </P>

    <H2>13. Contact Information</H2>
    <P>
      For questions or support regarding these Terms, please contact us at: <a href="mailto:support@fundedindex.com" className="text-[#ff9a2f] hover:text-[#ff7a00]">support@fundedindex.com</a>
    </P>
  </LegalLayout>
);

// ===================== POLICIES =====================
export const PoliciesPage = () => (
  <LegalLayout
    eyebrow="Legal"
    title="Company Policies"
    effectiveDate="October 31, 2025"
    testid="policies-page"
  >
    <P className="text-[#8a8a8a]">Privacy Policy &amp; Anti-Money Laundering (AML) Policy</P>

    <Hr />
    <H2>Privacy Policy</H2>
    <P>
      The Company reserves the right to suspend, replace, modify, amend, or terminate this Privacy Policy at any time, without notice, and within its sole and absolute discretion. Your continued use of the Services after such changes constitutes your agreement to the updated policy.
    </P>

    <H3>Information We Collect</H3>
    <P>
      We may collect personal data such as your name, contact information, and usage data when you use our Services. We reserve the right to modify data collection practices as necessary to improve user experience and platform security.
    </P>

    <H3>Cookies</H3>
    <P>
      Our website uses cookies to enhance functionality and personalization. You can disable cookies in your browser settings, but doing so may limit your access to some features.
    </P>

    <H3>Social Networking Data</H3>
    <P>
      If you connect via social media, we may collect information such as usernames, profile data, or pages you've interacted with — only as permitted by those platforms.
    </P>

    <H3>Disclosure and Use of Information</H3>
    <P>
      We may share your information with trusted service providers or to comply with legal obligations, such as subpoenas or regulatory requests. We never sell your data to third parties.
    </P>

    <H3>Security of Information</H3>
    <P>
      We employ industry-standard security measures to protect your personal data. However, no system is entirely secure, and we cannot guarantee absolute protection from unauthorized access.
    </P>

    <Hr />
    <H2>Anti-Money Laundering (AML) &amp; Know Your Client (KYC) Policy</H2>
    <P>
      For Index Traders is committed to preventing money laundering, terrorist financing, or other illicit activities. We operate ethically and in compliance with applicable business regulations.
    </P>

    <H3>Purpose and Objectives</H3>
    <UL>
      <li>Prevent misuse of our platform for illegal activities.</li>
      <li>Ensure compliance with global AML standards.</li>
      <li>Promote transparency and accountability in all business operations.</li>
    </UL>

    <H3>Know Your Client (KYC) Procedures</H3>
    <P>
      Before onboarding, For Index Traders verifies each client's identity through official documentation such as government-issued ID and proof of address. This ensures authenticity and compliance with AML obligations.
    </P>

    <H3>Internal Controls and Monitoring</H3>
    <P>
      We employ a risk-based approach for ongoing monitoring of client activity. Suspicious transactions are escalated to our compliance team for review.
    </P>

    <H3>Enhanced Customer Due Diligence</H3>
    <P>
      In higher-risk scenarios, we request additional documentation to verify the source of funds and wealth. Failure to provide this information may result in suspension or termination of the account.
    </P>

    <H3>Employee Training</H3>
    <P>
      All staff members receive ongoing AML/CFT training to identify and report suspicious activity promptly.
    </P>

    <H3>Policy Review</H3>
    <P>
      This policy is reviewed annually to ensure compliance with regulatory standards and evolving business needs.
    </P>

    <Hr />
    <P>
      For data or policy enquiries, contact <a href="mailto:support@fundedindex.com" className="text-[#ff9a2f] hover:text-[#ff7a00]">support@fundedindex.com</a>.
    </P>
  </LegalLayout>
);

// ===================== DISCLAIMER =====================
export const DisclaimerPage = () => (
  <LegalLayout
    eyebrow="Legal"
    title="Risk Disclaimer"
    effectiveDate="October 31, 2025"
    testid="disclaimer-page"
  >
    <P>
      The information provided by <b className="text-[#f5f5f5]">For Index Traders</b> ("the Company," "we," "our," or "us") on this website and through our Services is for general informational and educational purposes only. By accessing the platform or purchasing any of our challenge programs, you acknowledge and accept the following disclaimer in full.
    </P>

    <Hr />
    <H2>No Financial Advice</H2>
    <P>
      For Index Traders does not provide investment, financial, tax, legal, or trading advice. Nothing on this platform constitutes a solicitation, recommendation, endorsement, or offer to buy or sell any securities or financial instruments. You are solely responsible for your own trading decisions and outcomes.
    </P>

    <H2>Simulated Trading Environment</H2>
    <P>
      All trading on For Index Traders takes place exclusively on <b>Deriv MT5 demo accounts</b> that simulate live market conditions. These accounts are <b>not live brokerage accounts</b>. No real-market orders are executed on your behalf, and no real client capital is at risk during the evaluation or funded phase. Payouts are made directly by For Index Traders based on simulated performance — not by any broker or third party.
    </P>

    <H2>Not a Broker</H2>
    <P>
      For Index Traders is <b>not a broker</b>, dealer, financial institution, or licensed investment advisor. We do not accept deposits or hold client funds for trading purposes. We are not affiliated with Deriv or any regulated broker. Any fees collected are strictly for participation in our evaluation challenges and simulated funded programs.
    </P>

    <H2>Risk of Loss</H2>
    <UL>
      <li>Trading synthetic indices and other derivative instruments involves substantial risk and is not suitable for every investor.</li>
      <li>Past performance is not indicative of future results. No representation is made that any trader will or is likely to achieve profits or losses similar to those discussed on this site.</li>
      <li>You should never trade with money you cannot afford to lose, and you should fully understand the risks involved before participating.</li>
      <li>Challenge fees are one-time and non-refundable once trading credentials have been issued.</li>
    </UL>

    <H2>Hypothetical Performance Disclaimer</H2>
    <P>
      Any references to performance results, payout figures, or trader statistics on this website are <b>hypothetical or based on simulated trading</b>. Hypothetical or simulated performance results have certain inherent limitations and do not represent actual trading. Because the trades have not actually been executed in live markets, results may have under- or over-compensated for the impact of market factors such as lack of liquidity, slippage, or execution speed. No representation is being made that any account will or is likely to achieve profits or losses similar to those shown.
    </P>

    <H2>Forward-Looking Statements</H2>
    <P>
      Any forward-looking statements on this website are based on current expectations and assumptions and are subject to risks and uncertainties. Actual results may differ materially. For Index Traders disclaims any obligation to update these statements.
    </P>

    <H2>Third-Party Content</H2>
    <P>
      Our website may include content, links, or references to third-party platforms (such as Deriv, MT5, cryptocurrency networks, or payment processors). For Index Traders is not responsible for the accuracy, legality, or content of any external resources. Use of third-party services is at your own risk and subject to their own terms.
    </P>

    <H2>Regulatory Notice</H2>
    <P>
      For Index Traders operates as a proprietary trading evaluation firm. It is your responsibility to ensure that participation in our Services is legal in your country or jurisdiction. We reserve the right to restrict access to users from specific regions where required by local law.
    </P>

    <H2>Limitation of Liability</H2>
    <P>
      To the maximum extent permitted by applicable law, For Index Traders, its officers, employees, partners, and affiliates shall not be held liable for any direct, indirect, incidental, consequential, or punitive damages — including but not limited to loss of profits, data, or goodwill — arising from your use of the platform, reliance on any information provided, or inability to access the Services.
    </P>

    <Hr />
    <P>
      For questions about this disclaimer, contact us at <a href="mailto:support@fundedindex.com" className="text-[#ff9a2f] hover:text-[#ff7a00]">support@fundedindex.com</a>.
    </P>
  </LegalLayout>
);
